/**
 * Carrousel de la bande de reassurance, actif en mobile uniquement.
 *
 * Le defilement est fait par CSS (scroll-snap). Le script n'ajoute que deux
 * choses : la synchronisation des pastilles et l'avance automatique. S'il ne
 * se charge pas, la bande reste parcourable au doigt.
 *
 * Choix de conception : l'index courant est DEDUIT DE LA POSITION DE
 * DEFILEMENT, pas suivi dans une variable. Une premiere version s'appuyait
 * sur un IntersectionObserver ; il ne se declenchait pas quand une vue
 * redevenait visible sans avoir franchi le seuil, et les pastilles se
 * desynchronisaient. Lire la position est plus simple et toujours juste.
 */
const INTERVALLE = 5000;
const SEUIL_MOBILE = '(max-width: 749px)';

class EfReassurance extends HTMLElement {
  #piste = null;
  #pastilles = [];
  #minuteur = 0;
  #media = null;
  #rafEnCours = 0;
  /* Une fois que la visiteuse a pris la main, on ne la lui reprend plus. */
  #interactionUtilisateur = false;

  connectedCallback() {
    this.#piste = this.querySelector('[data-efr-piste]');
    this.#pastilles = [...this.querySelectorAll('[data-efr-pastille]')];
    if (!this.#piste || this.#pastilles.length < 2) return;

    this.#media = window.matchMedia(SEUIL_MOBILE);
    this.#media.addEventListener('change', this.#reprogrammer);

    this.#piste.addEventListener('scroll', this.#surDefilement, { passive: true });
    this.#piste.addEventListener('touchstart', this.#mainPrise, { passive: true });
    this.#piste.addEventListener('wheel', this.#mainPrise, { passive: true });

    this.#pastilles.forEach((p, i) =>
      p.addEventListener('click', () => {
        this.#mainPrise();
        this.#aller(i);
      })
    );

    this.#majPastilles();
    this.#reprogrammer();
  }

  disconnectedCallback() {
    this.#media?.removeEventListener('change', this.#reprogrammer);
    this.#piste?.removeEventListener('scroll', this.#surDefilement);
    this.#piste?.removeEventListener('touchstart', this.#mainPrise);
    this.#piste?.removeEventListener('wheel', this.#mainPrise);
    clearInterval(this.#minuteur);
    cancelAnimationFrame(this.#rafEnCours);
  }

  get #index() {
    const largeur = this.#piste.clientWidth || 1;
    const brut = Math.round(this.#piste.scrollLeft / largeur);
    return Math.min(Math.max(brut, 0), this.#pastilles.length - 1);
  }

  #surDefilement = () => {
    cancelAnimationFrame(this.#rafEnCours);
    this.#rafEnCours = requestAnimationFrame(this.#majPastilles);
  };

  #majPastilles = () => {
    const i = this.#index;
    this.#pastilles.forEach((p, n) => {
      p.classList.toggle('efr__pastille--active', n === i);
      p.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
  };

  #mainPrise = () => {
    this.#interactionUtilisateur = true;
    clearInterval(this.#minuteur);
  };

  #reprogrammer = () => {
    clearInterval(this.#minuteur);
    if (this.#interactionUtilisateur) return;
    if (!this.#media.matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#minuteur = setInterval(this.#suivant, INTERVALLE);
  };

  #suivant = () => {
    /* Onglet en arriere-plan : inutile de faire defiler dans le vide. */
    if (document.hidden) return;
    this.#aller((this.#index + 1) % this.#pastilles.length);
  };

  /**
   * On affecte scrollLeft plutot que d'appeler scrollTo({behavior:'smooth'}).
   * Le lissage est confie au CSS (scroll-behavior sur la piste) : si un
   * navigateur ou un reglage systeme desactive le defilement doux, l'affectation
   * saute a la bonne position au lieu de ne rien faire. Une version precedente
   * utilisait scrollTo en mode doux, et l'avance automatique restait figee
   * partout ou ce mode etait indisponible.
   */
  #aller(index) {
    const cible = index * this.#piste.clientWidth;
    this.#piste.scrollLeft = cible;

    /* Garde-fou : certains navigateurs, ou un reglage systeme, neutralisent
       le defilement lisse — l'affectation ne produit alors aucun mouvement et
       le carrousel reste fige. On verifie, et on repositionne sans lissage si
       rien n'a bouge. Constate en conditions reelles sur un navigateur ou
       scroll-behavior: smooth est inactif. */
    setTimeout(() => {
      const ecart = Math.abs(this.#piste.scrollLeft - cible);
      if (ecart > 4) {
        const lissageInitial = this.#piste.style.scrollBehavior;
        this.#piste.style.scrollBehavior = 'auto';
        this.#piste.scrollLeft = cible;
        this.#piste.style.scrollBehavior = lissageInitial;
      }
    }, 600);
  }
}

if (!customElements.get('ef-reassurance')) {
  customElements.define('ef-reassurance', EfReassurance);
}
